import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, Users, Settings, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: Home, end: true },
  { to: "/portal/groups", label: "Groups", icon: Users },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col lg:flex-row" data-testid="portal-layout">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#080808] sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
          <img src="/logo.png" alt="" className="w-10 h-10 rounded-full ring-1 ring-electric/40" />
          <div>
            <div className="font-display font-black text-sm tracking-tight">THE PUSH</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-electric">Member Portal</div>
          </div>
        </Link>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? "bg-electric/10 text-electric border border-electric/20"
                    : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
              data-testid={`portal-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="px-4 py-2 mb-2">
            <div className="text-sm font-bold text-white truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-zinc-400 hover:text-electric hover:bg-electric/5 transition" data-testid="portal-logout">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* MOBILE TOP BAR */}
      <header className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#080808] sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="w-8 h-8 rounded-full" />
          <span className="font-display font-bold text-sm">PORTAL</span>
        </Link>
        <button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" className="text-white">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {mobileOpen && (
        <div className="lg:hidden bg-black/95 border-b border-white/5 p-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
                  isActive ? "bg-electric/10 text-electric" : "text-zinc-300"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-sm text-zinc-400">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}

      <main id="main-content" className="flex-1 min-w-0 px-5 lg:px-10 py-8 lg:py-12 max-w-6xl">
        <Outlet />
      </main>
    </div>
  );
}
